import { zodResolver } from '@hookform/resolvers/zod';
import { ProposalPdfRequest, type QuoteDocument, type QuoteLine } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  Input,
  ScrollArea,
  toast,
} from '@kalos/ui';
import { EyeOpenIcon, PersonIcon } from '@radix-ui/react-icons';
import { format, parseISO } from 'date-fns';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useBatchUserQuery } from '../../../hooks/react-query/useUserQuery';
import { useMobileDevice } from '../../../hooks/useMedia';
import { debounce, ProposalClientService } from '../../../tools/helpers';
import {
  getQuoteDocumentTaxCost,
  getQuoteDocumentTaxRate,
} from '../../../utils/quoteDocumentPricing';
import { TechniciansComboboxPicker } from '../../ComponentsLibrary/Pickers/newPickers/TechnicianComboboxPicker';
import { techniciansUserFilter } from '../../ComponentsLibrary/Pickers/newPickers/utils';

// Schema for proposal review and send
const proposalReviewAndSendSchema = z.object({
  employeesCC: z.array(z.string()), // Array of email addresses
  selfCC: z.boolean().default(false),
  validUntilDate: z.string().min(1, 'Agreement valid until date is required'),
});

export type ProposalReviewAndSendFormData = z.infer<typeof proposalReviewAndSendSchema>;

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Email input component with autocomplete
interface EmailInputWithAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** Adds the provided email to the CC list */
  onAddEmail: (email: string) => void;
  disabled?: boolean;
  placeholder?: string;
  technicianEmails: Array<{ email: string; name: string }>;
}

const EmailInputWithAutocomplete: FC<EmailInputWithAutocompleteProps> = ({
  value,
  onChange,
  onAddEmail,
  disabled = false,
  placeholder = 'Enter email or search technician...',
  technicianEmails,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>(300);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter technicians based on input value (prefix match).
  const filteredTechnicians: Array<{ email: string; name: string }> = useMemo(() => {
    const searchTerm = value.toLowerCase().trim();
    if (searchTerm === '') return [];

    return technicianEmails.filter((tech) => {
      const emailLower = tech.email.toLowerCase();
      const nameParts = tech.name.toLowerCase().split(' ');
      return (
        emailLower.startsWith(searchTerm) || nameParts.some((part) => part.startsWith(searchTerm))
      );
    });
  }, [value, technicianEmails]);

  // Validate email on change
  useEffect(() => {
    setIsValidEmail(EMAIL_REGEX.test(value));
  }, [value]);

  // Improved viewport positioning for mobile
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom - 10;
      const spaceAbove = rect.top - 10;

      // Determine position based on available space
      if (spaceBelow >= 200 || spaceBelow > spaceAbove) {
        setDropdownPosition('bottom');
        setDropdownMaxHeight(Math.min(300, Math.max(100, spaceBelow)));
      } else {
        setDropdownPosition('top');
        setDropdownMaxHeight(Math.min(300, Math.max(100, spaceAbove)));
      }
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    onChange(newVal);
    setIsOpen(newVal.trim() !== '');
  };

  const handleInputFocus = () => {
    if (value && filteredTechnicians.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Increased delay for mobile touch events
    setTimeout(() => setIsOpen(false), 200);
  };

  const handleSelectTechnician = (email: string) => {
    onAddEmail(email);
    // Clear input and close.
    onChange('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isValidEmail) {
      e.preventDefault();
      onAddEmail(value);
    }
  };

  // Handle touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        type="email"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        className={`${isValidEmail && value ? 'border-green-500' : ''} ${
          value && !isValidEmail ? 'border-red-500' : ''
        }`}
      />
      {isOpen && (
        <div
          className={`absolute z-[9999] w-full rounded-md border border-gray-200 bg-white shadow-lg ${
            dropdownPosition === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1'
          }`}
          style={{
            maxHeight: `${dropdownMaxHeight}px`,
            overflowY: 'auto',
          }}
        >
          {filteredTechnicians.length > 0 ? (
            filteredTechnicians.map((tech) => (
              <button
                key={tech.email}
                type="button"
                className="w-full touch-manipulation px-3 py-3 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none active:bg-gray-200"
                onClick={() => handleSelectTechnician(tech.email)}
                onTouchStart={handleTouchStart}
              >
                <div className="font-medium">{tech.name}</div>
                <div className="text-xs text-gray-500">{tech.email}</div>
              </button>
            ))
          ) : (
            // No technician suggestions – show email tip / add option
            <button
              type="button"
              disabled={!isValidEmail}
              className={`w-full touch-manipulation px-3 py-3 text-left text-sm focus:outline-none ${
                isValidEmail ? 'hover:bg-gray-100 active:bg-gray-200' : 'cursor-default'
              }`}
              onClick={() => isValidEmail && handleSelectTechnician(value)}
              onTouchStart={handleTouchStart}
            >
              {isValidEmail ? (
                <span className="font-semibold">Add &ldquo;{value}&rdquo;</span>
              ) : (
                <>
                  <div className="text-xs font-medium text-gray-500">Email Address</div>
                  <div className="text-xs text-gray-400">
                    Enter email format (e.g., name@company.com)
                  </div>
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface ProposalReviewAndSendProps {
  entries: QuoteLine[];
  proposalFileName: string;
  onSubmit: (data: ProposalReviewAndSendFormData, inPerson?: boolean) => void;
  onCancel: () => void;
  loading?: boolean;
  disabled?: boolean;
  clientInfo: {
    displayName?: string;
    notes?: string;
    withJobNotes: boolean;
    includeAttachments: boolean;
    preparedById?: number;
    displayBusinessName: boolean;
    validUntilDays: number;
    validUntilDate?: string;
    quoteExpireDate?: string;
    blockLineItems?: boolean;
  };
  quoteDocument?: QuoteDocument | null;
  customer: {
    firstname?: string;
    lastname?: string;
    businessname?: string;
  };
  onClientInfoChange: (clientInfo: {
    displayName?: string;
    notes?: string;
    withJobNotes: boolean;
    includeAttachments: boolean;
    preparedById?: number;
    displayBusinessName: boolean;
    validUntilDays: number;
    validUntilDate?: string;
    quoteExpireDate?: string;
    blockLineItems?: boolean;
  }) => void;
  /** Persist only the proposal expiration date for the active proposal. */
  onExpirationDateChange: (value: string) => Promise<void>;
  /** Persist only the agreement valid-until (length of agreement) date for the active proposal. */
  onValidUntilDateChange: (value: string) => Promise<void>;
  serviceItem?: {
    discount?: string;
    discountcost?: string;
  };
}

// Helper function to validate date strings and provide a fallback
const isValidDate = (dateString: string) => {
  const date = new Date(dateString);
  // Check if the date is valid and not from the distant past
  return !isNaN(date.getTime()) && date.getFullYear() > 1970;
};

export const ProposalReviewAndSend: FC<ProposalReviewAndSendProps> = ({
  entries,
  proposalFileName,
  onSubmit,
  onCancel,
  loading = false,
  disabled = false,
  clientInfo,
  quoteDocument,
  customer,
  onClientInfoChange,
  onExpirationDateChange,
  onValidUntilDateChange,
  serviceItem,
}) => {
  const [emailInputValue, setEmailInputValue] = useState<string>('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isUpdatingAgreement, setIsUpdatingAgreement] = useState(false);
  const [isUpdatingValidUntil, setIsUpdatingValidUntil] = useState(false);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);

  const isMobile = useMobileDevice();

  // Get the event ID from the quote document
  const eventId = useMemo(() => quoteDocument?.eventId || 0, [quoteDocument]);

  // Check if PreparedBy is filled - this will control form submission
  const isPreparedByValid = useMemo(() => {
    return clientInfo.preparedById && clientInfo.preparedById > 0;
  }, [clientInfo.preparedById]);

  // Check if proposal has expired
  const isProposalExpired = useMemo(() => {
    if (!clientInfo.quoteExpireDate) {
      return false; // No expiration date set, so not expired
    }

    const expirationDate = new Date(clientInfo.quoteExpireDate);
    const today = new Date();

    // Reset time to compare dates only
    const expirationDateOnly = new Date(
      expirationDate.getFullYear(),
      expirationDate.getMonth(),
      expirationDate.getDate(),
    );
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Check if today is after the expiration date
    return todayOnly > expirationDateOnly;
  }, [clientInfo.quoteExpireDate]);

  // Form for proposal review options
  const form = useForm<ProposalReviewAndSendFormData>({
    resolver: zodResolver(proposalReviewAndSendSchema),
    defaultValues: {
      employeesCC: [],
      selfCC: false,
      validUntilDate: '',
    },
  });

  const techniciansQuery = useBatchUserQuery({
    filters: techniciansUserFilter,
  });

  // Debounced toast notifications
  const debouncedAgreementSuccessToast = useMemo(
    () =>
      debounce(() => {
        toast({
          title: 'Agreement Valid Until updated',
          description: 'The agreement valid until date has been updated',
          variant: 'success',
        });
      }, 1000),
    [],
  );

  const debouncedExpirationSuccessToast = useMemo(
    () =>
      debounce(() => {
        toast({
          title: 'Expiration date updated',
          description: 'The proposal expiration date has been updated',
          variant: 'success',
        });
      }, 1000),
    [],
  );

  // Expiration date is now directly from clientInfo
  const expirationDate = useMemo(() => {
    if (clientInfo.quoteExpireDate && isValidDate(clientInfo.quoteExpireDate)) {
      return new Date(clientInfo.quoteExpireDate);
    }
    // fallback to today if not set
    return new Date();
  }, [clientInfo.quoteExpireDate]);

  // Agreement valid until date is directly from clientInfo
  const agreementValidUntilDate = useMemo(() => {
    if (clientInfo.validUntilDate && isValidDate(clientInfo.validUntilDate)) {
      return new Date(clientInfo.validUntilDate);
    }
    // fallback to today if not set
    return new Date();
  }, [clientInfo.validUntilDate]);

  // Reference date for Days Valid
  const referenceDate = useMemo(() => {
    if (quoteDocument?.createdDate && isValidDate(quoteDocument.createdDate)) {
      return new Date(quoteDocument.createdDate);
    }
    return new Date();
  }, [quoteDocument?.createdDate]);

  // Days Valid is the difference between expiration date and reference date
  const daysValidText = useMemo(() => {
    const diffTime = expirationDate.getTime() - referenceDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays > 0 ? diffDays : 0} days from ${format(referenceDate, 'MM/dd/yyyy')}`;
  }, [expirationDate, referenceDate]);

  // Set initial agreement valid until date in form
  useEffect(() => {
    form.setValue('validUntilDate', agreementValidUntilDate.toISOString().split('T')[0]);
  }, [agreementValidUntilDate, form]);

  // Handle agreement valid until date change
  const handleAgreementValidUntilChange = async (value: string) => {
    setIsUpdatingAgreement(true);
    try {
      const date = parseISO(value);
      // Persist ONLY the valid-until date so a sibling edit (expiration) or the
      // other linked proposal is never overwritten with a stale value.
      await onValidUntilDateChange(format(date, 'yyyy-MM-dd 00:00:00'));
      debouncedAgreementSuccessToast();
    } catch (error) {
      console.error('Error updating agreement valid until date:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update agreement valid until date',
        variant: 'destructive',
      });
      form.setValue('validUntilDate', agreementValidUntilDate.toISOString().split('T')[0]);
    } finally {
      setIsUpdatingAgreement(false);
    }
  };

  // Handle expiration date change
  const handleExpirationDateChange = async (value: string) => {
    setIsUpdatingValidUntil(true);
    try {
      const date = parseISO(value);
      // Persist ONLY the expiration date so a sibling edit (valid-until) or the
      // other linked proposal is never overwritten with a stale value.
      await onExpirationDateChange(format(date, 'yyyy-MM-dd 00:00:00'));
      debouncedExpirationSuccessToast();
    } catch (error) {
      console.error('Error updating expiration date:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update expiration date',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingValidUntil(false);
    }
  };

  // Handle prepared by change
  const handlePreparedByChange = (userId: number | undefined) => {
    onClientInfoChange({
      ...clientInfo,
      preparedById: userId,
    });
  };

  // Calculate totals - parse adjustment string to number and respect quantities
  const subtotal = entries.reduce((sum, entry) => {
    const adjustment = entry.adjustment || '0';
    // Remove any $ and commas before parsing
    const cleanedValue = adjustment.replace(/[$,]/g, '');
    const value = parseFloat(cleanedValue);
    const quantity = entry.quotedQuantity || 1;
    return sum + (isNaN(value) ? 0 : value * quantity);
  }, 0);

  const discountPercent = parseFloat(
    quoteDocument?.id ? quoteDocument.discount || '0' : serviceItem?.discount || '0',
  );
  const discountCostValue = parseFloat(
    quoteDocument?.id ? quoteDocument.discountcost || '0' : serviceItem?.discountcost || '0',
  );
  const excludedSubtotal = entries.reduce((sum, entry) => {
    if (entry.excludeInvoiceDiscount !== 1) return sum;
    const adjustment = entry.adjustment || '0';
    const cleanedValue = adjustment.replace(/[$,]/g, '');
    const value = parseFloat(cleanedValue);
    const quantity = entry.quotedQuantity || 1;
    return sum + (isNaN(value) ? 0 : value * quantity);
  }, 0);
  const discountableBase = subtotal - excludedSubtotal;
  const discountAmount =
    discountPercent > 0 ? discountableBase * (discountPercent / 100) : discountCostValue;
  const afterDiscount = subtotal - discountAmount;
  const taxRateValue = quoteDocument?.id ? getQuoteDocumentTaxRate(quoteDocument) : 0;
  const taxCostFlat = quoteDocument?.id ? getQuoteDocumentTaxCost(quoteDocument) : 0;
  const taxAmount = taxRateValue > 0 ? afterDiscount * (taxRateValue / 100) : taxCostFlat;
  const total = afterDiscount + taxAmount;

  // Handle adding an employee to CC list
  const handleAddEmployeeToCC = (email: string) => {
    if (!email || !EMAIL_REGEX.test(email)) {
      toast({
        title: 'Invalid email address',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    const currentEmails = form.getValues('employeesCC');
    if (currentEmails.includes(email)) {
      toast({
        title: 'Email already added',
        description: 'This email is already in the CC list',
        variant: 'destructive',
      });
      return;
    }

    form.setValue('employeesCC', [...currentEmails, email]);
    setEmailInputValue('');
  };

  // Handle removing an employee from CC list
  const handleRemoveEmployeeFromCC = (email: string) => {
    const currentEmails = form.getValues('employeesCC');
    form.setValue(
      'employeesCC',
      currentEmails.filter((e) => e !== email),
    );
  };

  // Handle submit confirmation
  const handleSubmitConfirm = () => {
    // Check if proposal is expired
    if (isProposalExpired) {
      toast({
        title: 'Proposal Expired',
        description:
          'This proposal has expired and cannot be sent to the customer. Please update the expiration date or create a new proposal.',
        variant: 'destructive',
      });
      return;
    }

    setIsConfirmOpen(false);
    onSubmit(form.getValues());
  };

  // Handle review with customer submission
  // Validate the form before proceeding so the same rules that apply to "Submit and Send"
  // (e.g. required dates) are enforced when sharing in-person with the customer.
  const handleReviewWithCustomerSubmit = () => {
    // Check if proposal is expired
    if (isProposalExpired) {
      toast({
        title: 'Proposal Expired',
        description:
          'This proposal has expired and cannot be shared with the customer. Please update the expiration date or create a new proposal.',
        variant: 'destructive',
      });
      return;
    }

    form.handleSubmit((data) => {
      onSubmit(data, true); // Pass true for inPerson flag
    })();
  };

  // Calculate the display name
  const displayName =
    clientInfo.displayName ||
    (clientInfo.displayBusinessName && customer?.businessname && customer.businessname.trim() !== ''
      ? customer.businessname
      : `${customer?.firstname || ''} ${customer?.lastname || ''}`.trim());

  // Mobile-friendly financial summary
  const renderMobileFinancialSummary = () => {
    return (
      <div className="space-y-3">
        {entries.map((entry, index) => {
          const price = parseFloat(entry.adjustment || '0');
          const quantity = entry.quotedQuantity || 1;
          const lineTotal = price * quantity;

          return (
            <div key={index} className="rounded border bg-white p-3">
              <div className="mb-2 overflow-hidden whitespace-normal text-wrap break-words text-sm font-medium">
                {entry.description}
              </div>
              <div className="space-y-1 text-sm">
                <div className="text-gray-600">
                  {quantity > 1
                    ? `Qty: ${quantity} × $${price.toFixed(2)}`
                    : `Price: $${price.toFixed(2)}`}
                </div>
                <div className="font-medium">Total: ${lineTotal.toFixed(2)}</div>
              </div>
            </div>
          );
        })}

        {/* Totals Section */}
        <div className="space-y-2 rounded border-t-2 bg-gray-50 p-3">
          <div className="flex items-center justify-between text-sm">
            <div>Subtotal</div>
            <div>${subtotal.toFixed(2)}</div>
          </div>
          {(discountPercent > 0 || discountCostValue > 0) && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div>
                Discount (
                {discountPercent > 0
                  ? `${discountPercent}%`
                  : `$${discountCostValue.toFixed(2)} Flat`}
                )
              </div>
              <div>-${discountAmount.toFixed(2)}</div>
            </div>
          )}
          {(taxRateValue > 0 || taxCostFlat > 0) && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div>{taxRateValue > 0 ? `Tax (${taxRateValue}%)` : 'Tax (flat)'}</div>
              <div>${taxAmount.toFixed(2)}</div>
            </div>
          )}
          <div className="flex items-center justify-between border-t pt-2 font-bold">
            <div>Total</div>
            <div>${total.toFixed(2)}</div>
          </div>
        </div>
      </div>
    );
  };

  // Desktop financial summary table
  const renderDesktopFinancialSummary = () => {
    return (
      <div className="overflow-hidden rounded border border-gray-200">
        {/* Header Row */}
        <div className="grid grid-cols-[2fr_0.8fr_1fr] border-b border-gray-300 bg-gray-100 px-2 py-2 font-semibold text-gray-700">
          <div className="text-sm">Description</div>
          <div className="text-center text-sm">Quantity × Price</div>
          <div className="text-right text-sm">Total</div>
        </div>

        {/* Scrollable Content */}
        <div className="max-h-[250px] overflow-y-auto">
          {entries.map((entry, index) => {
            const price = parseFloat(entry.adjustment || '0');
            const quantity = entry.quotedQuantity || 1;
            const lineTotal = price * quantity;

            return (
              <div
                key={index}
                className="grid grid-cols-[2fr_0.8fr_1fr] border-b border-gray-200 bg-white px-2 py-3 hover:bg-gray-50"
              >
                <div className="overflow-hidden text-ellipsis whitespace-nowrap pr-2 text-sm font-medium">
                  {entry.description}
                </div>
                <div className="whitespace-nowrap text-center text-sm">
                  {quantity > 1 ? `${quantity} × $${price.toFixed(2)}` : `$${price.toFixed(2)}`}
                </div>
                <div className="whitespace-nowrap text-right text-sm font-semibold">
                  ${lineTotal.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Totals Section */}
        <div className="border-t-2 border-gray-400 bg-gray-50">
          {/* Subtotal Row */}
          <div className="grid grid-cols-[2fr_0.8fr_1fr] border-b border-gray-300 px-2 py-2 text-black">
            <div className="text-sm">Subtotal</div>
            <div></div>
            <div className="text-right text-sm font-medium">${subtotal.toFixed(2)}</div>
          </div>
          {(discountPercent > 0 || discountCostValue > 0) && (
            <div className="grid grid-cols-[2fr_0.8fr_1fr] border-b border-gray-300 px-2 py-2 text-gray-600">
              <div className="text-sm">
                Discount (
                {discountPercent > 0
                  ? `${discountPercent}%`
                  : `$${discountCostValue.toFixed(2)} Flat`}
                )
              </div>
              <div></div>
              <div className="text-right text-sm">-${discountAmount.toFixed(2)}</div>
            </div>
          )}
          {(taxRateValue > 0 || taxCostFlat > 0) && (
            <div className="grid grid-cols-[2fr_0.8fr_1fr] border-b border-gray-300 px-2 py-2 text-gray-600">
              <div className="text-sm">
                {taxRateValue > 0 ? `Tax (${taxRateValue}%)` : 'Tax (flat)'}
              </div>
              <div></div>
              <div className="text-right text-sm">${taxAmount.toFixed(2)}</div>
            </div>
          )}
          <div className="grid grid-cols-[2fr_0.8fr_1fr] px-2 py-3 font-bold text-black">
            <div className="text-sm">Total</div>
            <div></div>
            <div className="text-right text-lg">${total.toFixed(2)}</div>
          </div>
        </div>
      </div>
    );
  };

  // Handle download button click
  const handleDownloadClick = async () => {
    if (eventId === 0) return;
    if (!quoteDocument?.id) return;

    setIsDownloadLoading(true);
    try {
      // Create request directly instead of using refetch
      const request = ProposalPdfRequest.create();
      request.jobId = eventId;
      request.quoteDocumentId = quoteDocument.id;

      // Call service directly
      const response = await ProposalClientService.getProposalPreview(request);

      if (response && response.url) {
        const link = document.createElement('a');
        link.href = response.url;
        link.download = `proposal-${proposalFileName || eventId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('Failed to generate proposal preview URL');
      }
    } catch (error) {
      console.error(error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download the proposal. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloadLoading(false);
    }
  };

  // Handle preview button click
  const handlePreviewClick = async () => {
    if (eventId === 0) return;
    if (!quoteDocument?.id) return;

    setIsPreviewLoading(true);

    try {
      // Create request directly instead of using refetch
      const request = ProposalPdfRequest.create();
      request.jobId = eventId;
      request.quoteDocumentId = quoteDocument.id;

      // Call service directly
      const response = await ProposalClientService.getProposalPreview(request);

      if (response && response.url) {
        // For mobile devices, open PDF in new tab for better viewing experience
        if (isMobile) {
          window.open(response.url, '_blank');
        } else {
          // For desktop, use the dialog preview
          // Instead of opening in new tab, fetch and create a blob URL
          const pdfResponse = await fetch(response.url);
          if (!pdfResponse.ok) {
            throw new Error('Failed to fetch PDF content');
          }

          const arrayBuffer = await pdfResponse.arrayBuffer();
          const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

          // Create and store the blob URL
          const blobUrl = URL.createObjectURL(blob);

          // Clean up old URL if exists
          if (pdfPreviewUrl) {
            URL.revokeObjectURL(pdfPreviewUrl);
          }

          setPdfPreviewUrl(blobUrl);
          setIsPdfPreviewOpen(true);
        }
      } else {
        throw new Error('Failed to generate proposal preview URL');
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: 'Preview Failed',
        description: 'Failed to generate preview. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Cleanup preview blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Proposal Summary Card - Left Column */}
        <Card className="h-full bg-white p-3">
          <h2 className="mb-2 text-lg font-semibold">Proposal Summary</h2>

          <div className="space-y-4">
            {/* Client Information Section */}
            <div className="rounded border bg-gray-50 p-4">
              <h3 className="mb-3 text-base font-semibold">Proposal Details</h3>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1 font-medium text-gray-600">Client Name:</div>
                  <div className="col-span-2">{displayName}</div>
                </div>

                {customer?.businessname && (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-1 font-medium text-gray-600">Business Name:</div>
                    <div className="col-span-2">{customer.businessname}</div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1 font-medium text-gray-600">Valid Until:</div>
                  <div className="col-span-2">{format(expirationDate, 'MM/dd/yyyy')}</div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1 font-medium text-gray-600">
                    The length of this agreement:
                  </div>
                  <div className="col-span-2">{format(agreementValidUntilDate, 'MM/dd/yyyy')}</div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1 font-medium text-gray-600">Proposal ID:</div>
                  <div className="col-span-2">{proposalFileName}</div>
                </div>
              </div>
            </div>

            {/* Financial Summary Section */}
            <div className="rounded border bg-gray-50 p-4">
              <h3 className="mb-3 text-base font-semibold">Financial Summary</h3>

              {/* Line Items - Use conditional rendering based on screen size */}
              {isMobile ? renderMobileFinancialSummary() : renderDesktopFinancialSummary()}
            </div>

            {/* Download/Preview Buttons */}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                className="flex-1 bg-white text-sm sm:text-base"
                disabled={disabled || eventId === 0 || !quoteDocument?.id || isDownloadLoading}
                isLoading={isDownloadLoading}
                onClick={handleDownloadClick}
              >
                <svg
                  className="mr-2 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 16.5V18.75C3 19.3467 3.23705 19.919 3.65901 20.341C4.08097 20.7629 4.65326 21 5.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V16.5M16.5 12L12 16.5M12 16.5L7.5 12M12 16.5V3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="truncate">Download PDF</span>
              </Button>

              <Button
                type="button"
                variant="outline"
                className="flex-1 bg-white text-sm sm:text-base"
                disabled={disabled || eventId === 0 || !quoteDocument?.id || isPreviewLoading}
                isLoading={isPreviewLoading}
                onClick={handlePreviewClick}
              >
                <EyeOpenIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  {isMobile ? 'Preview (Opens in New Tab)' : 'Print/Preview'}
                </span>
              </Button>
            </div>
          </div>
        </Card>

        {/* Proposal Settings Card - Right Column */}
        <Card className="h-full bg-white p-3">
          <h2 className="mb-3 text-lg font-semibold">Proposal Settings</h2>

          <FormContext {...form}>
            <form onSubmit={form.handleSubmit(() => setIsConfirmOpen(true))} className="space-y-4">
              {/* Two Column Layout for Top Fields - Responsive on mobile */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Prepared By Section */}
                <div>
                  <h3 className="mb-2 text-sm font-medium">
                    Prepared By <span className="text-red-500">*</span>
                  </h3>
                  <TechniciansComboboxPicker
                    selected={clientInfo.preparedById ? clientInfo.preparedById.toString() : ''}
                    onSelect={(user) => handlePreparedByChange(user?.id)}
                    disabled={disabled}
                    className="bg-white"
                    contentClassName="bg-white"
                  />
                  {!isPreparedByValid && (
                    <p className="mt-1 text-xs text-red-500">Prepared By is required</p>
                  )}
                </div>

                {/* From Sender Section */}
                <div>
                  <h3 className="mb-2 text-sm font-medium">From Sender</h3>
                  <input
                    type="email"
                    readOnly
                    className="border-input ring-offset-background h-9 w-full rounded-md border bg-gray-100 px-3 py-1 text-sm"
                    value="office@kalosflorida.com"
                  />
                </div>
              </div>

              {/* Dates Section - Two Columns - Responsive on mobile */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Expiration Date Section */}
                <div>
                  <h3 className="mb-2 text-sm font-medium">
                    Expiration Date <span className="text-red-500">*</span>
                  </h3>
                  <div className="relative">
                    <div className="border-input ring-offset-background flex h-9 w-full items-center rounded-md border bg-white">
                      <input
                        type="date"
                        className="flex-1 bg-transparent px-3 py-1 text-left text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        value={expirationDate.toISOString().split('T')[0]}
                        min={new Date().toISOString().split('T')[0]}
                        disabled={disabled || isUpdatingValidUntil}
                        required
                        // Block manual keyboard entry; the date can only be set via the
                        // calendar picker. Tab is allowed so keyboard navigation still works.
                        onKeyDown={(e) => {
                          if (e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleExpirationDateChange(e.target.value);
                          }
                        }}
                      />
                      {isUpdatingValidUntil && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <div className="border-t-primary h-4 w-4 animate-spin rounded-full border-2 border-gray-300"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">When the proposal expires</p>
                </div>

                {/* Days Valid Section */}
                <div>
                  <h3 className="mb-2 text-sm font-medium">Days Valid</h3>
                  <input
                    type="text"
                    className="border-input ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-gray-100 px-3 py-1 text-left text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    readOnly
                    value={daysValidText}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    {quoteDocument?.createdDate && isValidDate(quoteDocument.createdDate)
                      ? `From: ${format(referenceDate, 'MM/dd/yyyy')}`
                      : 'From today'}
                  </p>
                </div>
              </div>

              {/* Agreement Valid Until Section */}
              <div>
                <h3 className="mb-2 text-sm font-medium">
                  The length of this agreement <span className="text-red-500">*</span>
                </h3>
                <FormField
                  control={form.control}
                  name="validUntilDate"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <div className="relative">
                          <div className="border-input ring-offset-background flex h-9 w-full items-center rounded-md border bg-white">
                            <input
                              type="date"
                              className="flex-1 bg-transparent px-3 py-1 text-left text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                              min={new Date().toISOString().split('T')[0]}
                              disabled={disabled || isUpdatingAgreement}
                              required
                              {...field}
                              // Block manual keyboard entry; the date can only be set via the
                              // calendar picker. Tab is allowed so keyboard navigation still works.
                              onKeyDown={(e) => {
                                if (e.key !== 'Tab') {
                                  e.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                field.onChange(e);
                                if (e.target.value) {
                                  handleAgreementValidUntilChange(e.target.value);
                                }
                              }}
                            />
                            {isUpdatingAgreement && (
                              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                <div className="border-t-primary h-4 w-4 animate-spin rounded-full border-2 border-gray-300"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </FormControl>
                      {fieldState.error && (
                        <p className="mt-1 text-xs text-red-500">{fieldState.error.message}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        When the agreement expires after approval
                      </p>
                    </FormItem>
                  )}
                />
              </div>

              {/* Include Options - Side by Side, becomes vertical on mobile */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center justify-between rounded border border-white bg-white p-2">
                  <h3 className="text-sm font-medium">Include Job/Technician Notes</h3>
                  <div className="relative">
                    {isUpdatingSettings && (
                      <div className="absolute -right-1 -top-1 z-10">
                        <div className="border-t-primary h-3 w-3 animate-spin rounded-full border-2 border-gray-300"></div>
                      </div>
                    )}
                    <div
                      className={`h-6 w-12 rounded-full ${clientInfo.withJobNotes ? 'bg-primary' : 'bg-gray-300'} ${isUpdatingSettings ? 'opacity-70' : 'cursor-pointer'}`}
                      onClick={async () => {
                        if (isUpdatingSettings) return;
                        setIsUpdatingSettings(true);
                        try {
                          const updatedInfo = {
                            ...clientInfo,
                            withJobNotes: !clientInfo.withJobNotes,
                          };
                          await onClientInfoChange(updatedInfo);
                        } finally {
                          setIsUpdatingSettings(false);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={clientInfo.withJobNotes}
                      aria-disabled={isUpdatingSettings}
                    >
                      <div
                        className={`h-6 w-6 rounded-full bg-white shadow transition-transform ${clientInfo.withJobNotes ? 'translate-x-6' : ''}`}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between rounded border border-white bg-white p-2">
                  <h3 className="text-sm font-medium">Include Attachments</h3>
                  <div className="relative">
                    {isUpdatingSettings && (
                      <div className="absolute -right-1 -top-1 z-10">
                        <div className="border-t-primary h-3 w-3 animate-spin rounded-full border-2 border-gray-300"></div>
                      </div>
                    )}
                    <div
                      className={`h-6 w-12 rounded-full ${clientInfo.includeAttachments ? 'bg-primary' : 'bg-gray-300'} ${isUpdatingSettings ? 'opacity-70' : 'cursor-pointer'}`}
                      onClick={async () => {
                        if (isUpdatingSettings) return;
                        setIsUpdatingSettings(true);
                        try {
                          const updatedInfo = {
                            ...clientInfo,
                            includeAttachments: !clientInfo.includeAttachments,
                          };
                          await onClientInfoChange(updatedInfo);
                        } finally {
                          setIsUpdatingSettings(false);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={clientInfo.includeAttachments}
                      aria-disabled={isUpdatingSettings}
                    >
                      <div
                        className={`h-6 w-6 rounded-full bg-white shadow transition-transform ${clientInfo.includeAttachments ? 'translate-x-6' : ''}`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Block Line Item Creation Toggle */}
              <div className="flex items-center justify-between rounded border border-white bg-white p-2">
                <div>
                  <h3 className="text-sm font-medium">Block Line Item Creation</h3>
                  <p className="text-xs text-gray-500">
                    Prevent new line items from being added after proposal is signed
                  </p>
                </div>
                <div className="relative flex-shrink-0">
                  {isUpdatingSettings && (
                    <div className="absolute -right-1 -top-1 z-10">
                      <div className="border-t-primary h-3 w-3 animate-spin rounded-full border-2 border-gray-300"></div>
                    </div>
                  )}
                  <div
                    className={`h-6 w-12 rounded-full ${clientInfo.blockLineItems ? 'bg-primary' : 'bg-gray-300'} ${isUpdatingSettings ? 'opacity-70' : 'cursor-pointer'}`}
                    onClick={async () => {
                      if (isUpdatingSettings) return;
                      setIsUpdatingSettings(true);
                      try {
                        const updatedInfo = {
                          ...clientInfo,
                          blockLineItems: !clientInfo.blockLineItems,
                        };
                        await onClientInfoChange(updatedInfo);
                      } finally {
                        setIsUpdatingSettings(false);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={clientInfo.blockLineItems}
                    aria-disabled={isUpdatingSettings}
                  >
                    <div
                      className={`h-6 w-6 rounded-full bg-white shadow transition-transform ${clientInfo.blockLineItems ? 'translate-x-6' : ''}`}
                    />
                  </div>
                </div>
              </div>

              {/* Expiration Alert */}
              {isProposalExpired && (
                <div className="rounded-md border border-red-200 bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Proposal Expired</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>
                          This proposal expired on {format(expirationDate, 'MM/dd/yyyy')}. Please
                          update the expiration date before sharing/sending.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Missing Prepared By Alert */}
              {!isPreparedByValid && !isProposalExpired && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-amber-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-amber-800">
                        Missing Required Information
                      </h3>
                      <div className="mt-2 text-sm text-amber-700">
                        <p>
                          Please select who prepared this proposal before sharing with the customer.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons - Responsive layout */}
              <div className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2">
                <Button
                  type="button"
                  className="bg-primary hover:bg-primary/90 w-full text-white disabled:opacity-50"
                  disabled={
                    disabled || loading || eventId === 0 || !isPreparedByValid || isProposalExpired
                  }
                  isLoading={loading}
                  onClick={handleReviewWithCustomerSubmit}
                  title={
                    !isPreparedByValid
                      ? 'Please select who prepared this proposal'
                      : isProposalExpired
                        ? 'This proposal has expired and cannot be shared with the customer'
                        : ''
                  }
                >
                  <PersonIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="truncate">Review with Customer</span>
                </Button>

                <Button
                  type="submit"
                  disabled={disabled || loading || !isPreparedByValid || isProposalExpired}
                  isLoading={loading}
                  className="bg-primary hover:bg-primary/90 text-white disabled:opacity-50"
                  title={
                    !isPreparedByValid
                      ? 'Please select who prepared this proposal'
                      : isProposalExpired
                        ? 'This proposal has expired and cannot be sent to the customer'
                        : ''
                  }
                >
                  Submit and Send
                </Button>
              </div>
            </form>
          </FormContext>

          {/* Confirmation Dialog */}
          <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
            <DialogContent className="overflow-visible bg-white p-0 sm:max-w-[450px]">
              <ScrollArea className="max-h-[80vh]">
                <div className="p-5">
                  <div className="mb-5 flex items-center justify-between">
                    <DialogTitle className="m-0 text-xl font-bold">
                      Confirm Proposal Submission
                    </DialogTitle>
                  </div>

                  <div className="space-y-5">
                    <p className="text-lg text-gray-700">
                      You are about to send this proposal to {displayName} for the amount of $
                      {total.toFixed(2)}.
                    </p>

                    <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
                      <p className="font-medium text-amber-800">
                        This action cannot be undone. The client will receive an email with the
                        proposal details.
                      </p>
                    </div>

                    {/* CC Options Section in Dialog */}
                    <div className="space-y-3">
                      <h3 className="text-base font-semibold">CC Recipients</h3>

                      <div className="pl-0">
                        <FormField
                          control={form.control}
                          name="selfCC"
                          render={({ field }) => (
                            <div className="flex items-center">
                              <span className="text-sm font-medium">
                                CC myself on this proposal
                              </span>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={disabled}
                                className="data-[state=checked]:border-primary data-[state=checked]:bg-primary ml-1 h-6 w-6 rounded-sm border-2 border-gray-300"
                              />
                            </div>
                          )}
                        />
                      </div>

                      <div className="space-y-2 pl-0">
                        {/* Display selected employees ABOVE the input */}
                        {form.watch('employeesCC').length > 0 && (
                          <div className="max-h-[120px] space-y-1 overflow-y-auto rounded border bg-white p-2">
                            {form.watch('employeesCC').map((email) => {
                              const user = techniciansQuery.data?.results.find(
                                (u) => u.email && u.email === email,
                              );
                              return (
                                <div
                                  key={email}
                                  className="flex items-center justify-between rounded border bg-gray-50 p-2 text-sm"
                                >
                                  <span>
                                    {user?.firstname} {user?.lastname} ({email})
                                  </span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveEmployeeFromCC(email)}
                                    disabled={disabled}
                                    className="h-6 p-0 hover:bg-gray-200"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <line x1="18" y1="6" x2="6" y2="18"></line>
                                      <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Email input */}
                        <EmailInputWithAutocomplete
                          value={emailInputValue}
                          onChange={setEmailInputValue}
                          onAddEmail={handleAddEmployeeToCC}
                          disabled={disabled || loading || techniciansQuery.isLoading}
                          technicianEmails={
                            techniciansQuery.data?.results
                              .map((u) => ({
                                email: u.email || '',
                                name: `${u.firstname || ''} ${u.lastname || ''}`.trim(),
                              }))
                              .filter((tech) => tech.email) || []
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <div className="flex justify-between p-6 pt-4">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-800 hover:bg-gray-50"
                  onClick={() => setIsConfirmOpen(false)}
                  disabled={disabled}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-primary hover:bg-primary/90 rounded-md px-6 py-3 text-base font-medium text-white"
                  onClick={handleSubmitConfirm}
                  disabled={disabled || loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                      Sending...
                    </span>
                  ) : (
                    'Send Proposal'
                  )}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </Card>
      </div>

      {/* PDF Preview Dialog */}
      {pdfPreviewUrl && (
        <Dialog open={isPdfPreviewOpen} onOpenChange={setIsPdfPreviewOpen}>
          <DialogContent
            className="flex max-h-[90vh] max-w-4xl flex-col bg-white p-0"
            keepFocusVisibleAboveKeyboard={false}
          >
            <DialogTitle className="flex-shrink-0 border-b p-4 text-xl font-bold">
              Proposal Preview
            </DialogTitle>
            <div className="min-h-0 flex-1 overflow-hidden">
              <iframe
                src={pdfPreviewUrl}
                className="h-full w-full border-0"
                title="Proposal Preview"
              ></iframe>
            </div>
            <div className="flex flex-shrink-0 justify-end gap-2 border-t p-4">
              <Button type="button" variant="outline" onClick={() => setIsPdfPreviewOpen(false)}>
                Close
              </Button>
              <Button
                type="button"
                onClick={handleDownloadClick}
                disabled={isDownloadLoading}
                isLoading={isDownloadLoading}
              >
                <svg
                  className="mr-2 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 16.5V18.75C3 19.3467 3.23705 19.919 3.65901 20.341C4.08097 20.7629 4.65326 21 5.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V16.5M16.5 12L12 16.5M12 16.5L7.5 12M12 16.5V3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Download PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
